import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useClientPortal } from '../../context/ClientPortalContext';
import { PortalApplicationsService } from '../../services/portal/portalApplicationsService';
import {
    ArrowLeftIcon,
    CalendarIcon,
    UserCircleIcon,
    CheckCircleIcon,
    DocumentTextIcon,
    ClockIcon,
    BriefcaseIcon,
    BuildingLibraryIcon,
    GlobeEuropeAfricaIcon,
    ChevronRightIcon,
    GiftIcon,
    ExclamationTriangleIcon,
    CheckBadgeIcon
} from '@heroicons/react/24/outline';

// Case Journey Tracker Component
const CaseJourneyTracker = ({ stages, currentStage }) => {
    if (!stages || stages.length === 0) return null;

    const getStageStatus = (stage) => {
        if (stage.status === 'done' || stage.completed_at) return 'completed';
        if (stage.status === 'active' || stage.stage_key === currentStage) return 'current';
        return 'pending';
    };

    return (
        <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm">
            <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wider mb-4 flex items-center gap-2">
                <CheckBadgeIcon className="h-4 w-4 text-indigo-500" />
                Case Progress
            </h3>
            <div className="flex items-center justify-between overflow-x-auto pb-2">
                {stages.map((stage, index) => {
                    const status = getStageStatus(stage);
                    const isLast = index === stages.length - 1;

                    return (
                        <React.Fragment key={stage.id}>
                            <div className="flex flex-col items-center min-w-[80px]">
                                <div className={`
                                    h-10 w-10 rounded-full flex items-center justify-center text-sm font-bold transition-all
                                    ${status === 'completed'
                                        ? 'bg-emerald-500 text-white'
                                        : status === 'current'
                                            ? 'bg-indigo-500 text-white ring-4 ring-indigo-100'
                                            : 'bg-slate-100 text-slate-400'}
                                `}>
                                    {status === 'completed' ? (
                                        <CheckCircleIcon className="h-5 w-5" />
                                    ) : (
                                        index + 1
                                    )}
                                </div>
                                <span className={`
                                    text-xs mt-2 text-center font-medium max-w-[80px]
                                    ${status === 'current' ? 'text-indigo-600' : status === 'completed' ? 'text-slate-700' : 'text-slate-400'}
                                `}>
                                    {stage.title}
                                </span>
                            </div>
                            {!isLast && (
                                <div className={`
                                    flex-1 h-1 mx-2 rounded-full min-w-[20px]
                                    ${status === 'completed' ? 'bg-emerald-300' : 'bg-slate-100'}
                                `} />
                            )}
                        </React.Fragment>
                    );
                })}
            </div>
        </div>
    );
};

// Offer Card Component
const OfferCard = ({ offer }) => {
    const getOfferBadge = () => {
        if (offer.accepted) return { text: 'Accepted', color: 'bg-emerald-100 text-emerald-700' };
        if (offer.offer_type === 'unconditional') return { text: 'Unconditional', color: 'bg-blue-100 text-blue-700' };
        if (offer.offer_type === 'conditional') return { text: 'Conditional', color: 'bg-amber-100 text-amber-700' };
        return { text: offer.status || 'Pending', color: 'bg-slate-100 text-slate-600' };
    };

    const badge = getOfferBadge();
    const isActionRequired = !offer.accepted && offer.expires_at && new Date(offer.expires_at) > new Date();

    return (
        <div className={`
            p-4 rounded-xl border transition-all
            ${isActionRequired ? 'border-amber-200 bg-amber-50/50' : 'border-slate-100 bg-white'}
        `}>
            <div className="flex items-start justify-between gap-4">
                <div className="flex items-center gap-3">
                    <div className={`
                        h-10 w-10 rounded-lg flex items-center justify-center
                        ${offer.accepted ? 'bg-emerald-100 text-emerald-600' : 'bg-indigo-50 text-indigo-600'}
                    `}>
                        <GiftIcon className="h-5 w-5" />
                    </div>
                    <div>
                        <h4 className="font-medium text-slate-900">
                            {offer.university_name || 'University Offer'}
                        </h4>
                        <p className="text-sm text-slate-500">{offer.program_name || 'Program'}</p>
                    </div>
                </div>
                <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${badge.color}`}>
                    {badge.text}
                </span>
            </div>

            <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                {offer.tuition_total && (
                    <div>
                        <span className="text-slate-500">Tuition:</span>
                        <span className="ml-1 font-medium text-slate-700">
                            {offer.currency || ''} {Number(offer.tuition_total).toLocaleString()}
                        </span>
                    </div>
                )}
                {offer.scholarship_amount && Number(offer.scholarship_amount) > 0 && (
                    <div>
                        <span className="text-slate-500">Scholarship:</span>
                        <span className="ml-1 font-medium text-emerald-600">
                            -{offer.currency || ''} {Number(offer.scholarship_amount).toLocaleString()}
                        </span>
                    </div>
                )}
                {offer.expires_at && (
                    <div>
                        <span className="text-slate-500">Expires:</span>
                        <span className="ml-1 font-medium text-slate-700">
                            {new Date(offer.expires_at).toLocaleDateString()}
                        </span>
                    </div>
                )}
                {offer.enrollment_deadline && (
                    <div>
                        <span className="text-slate-500">Enrollment by:</span>
                        <span className="ml-1 font-medium text-slate-700">
                            {new Date(offer.enrollment_deadline).toLocaleDateString()}
                        </span>
                    </div>
                )}
            </div>

            {isActionRequired && !offer.accepted && (
                <div className="mt-3 flex items-center gap-2 text-xs text-amber-700">
                    <ExclamationTriangleIcon className="h-4 w-4" />
                    <span>Action required - Please respond to this offer</span>
                </div>
            )}
        </div>
    );
};

// Document Progress Component
const DocumentProgress = ({ done, total, label }) => {
    if (total === 0) return null;
    const percentage = Math.round((done / total) * 100);

    return (
        <div className="flex items-center gap-2">
            <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                <div
                    className={`h-full transition-all ${percentage === 100 ? 'bg-emerald-500' : 'bg-indigo-500'}`}
                    style={{ width: `${percentage}%` }}
                />
            </div>
            <span className="text-xs text-slate-500 min-w-[45px]">
                {done}/{total} {label || 'docs'}
            </span>
        </div>
    );
};

const ClientCaseDetailPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { clientId } = useClientPortal();
    const [caseDetails, setCaseDetails] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('tasks');

    useEffect(() => {
        if (id && clientId) {
            loadCaseDetails();
        }
    }, [id, clientId]);

    const loadCaseDetails = async () => {
        setLoading(true);
        const { data, error } = await PortalApplicationsService.getCaseDetails(id);
        if (error) {
            console.error('Failed to load case details:', error);
        } else {
            setCaseDetails(data);
        }
        setLoading(false);
    };

    // Calculate total documents across all applications
    const totalDocs = useMemo(() => {
        if (!caseDetails) return { done: 0, total: 0 };
        let done = 0, total = 0;
        (caseDetails.uni_applications || []).forEach(app => {
            done += app.doc_progress?.done || 0;
            total += app.doc_progress?.total || 0;
        });
        (caseDetails.visa_applications || []).forEach(app => {
            done += app.doc_progress?.done || 0;
            total += app.doc_progress?.total || 0;
        });
        return { done, total };
    }, [caseDetails]);

    if (loading) {
        return (
            <div className="space-y-6 animate-pulse">
                <div className="h-8 bg-slate-100 rounded w-1/3"></div>
                <div className="h-64 bg-slate-100 rounded-2xl"></div>
            </div>
        );
    }

    if (!caseDetails) {
        return (
            <div className="text-center py-12">
                <h3 className="text-lg font-medium text-slate-900">Case not found</h3>
                <button
                    onClick={() => navigate('/portal/applications')}
                    className="mt-4 text-indigo-600 font-medium hover:text-indigo-500"
                >
                    Back to Applications
                </button>
            </div>
        );
    }

    const tabs = [
        { id: 'tasks', label: 'Tasks', icon: CheckCircleIcon, count: caseDetails.tasks?.length || 0 },
        {
            id: 'applications',
            label: 'Applications',
            icon: BuildingLibraryIcon,
            count: (caseDetails.uni_applications?.length || 0) + (caseDetails.visa_applications?.length || 0)
        },
        { id: 'offers', label: 'Offers', icon: GiftIcon, count: caseDetails.offers?.length || 0 },
        { id: 'documents', label: 'Documents', icon: DocumentTextIcon, count: totalDocs.total },
    ];

    return (
        <div className="max-w-5xl mx-auto space-y-6">
            {/* Header */}
            <div>
                <button
                    onClick={() => navigate('/portal/applications')}
                    className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-800 mb-4 transition-colors"
                >
                    <ArrowLeftIcon className="h-4 w-4" />
                    Back to Applications
                </button>

                <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm">
                    <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                        <div>
                            <div className="flex items-center gap-3 mb-2">
                                <h1 className="text-2xl font-bold text-slate-900">{caseDetails.title}</h1>
                                <span className={`px-3 py-1 text-sm font-medium rounded-full capitalize 
                                    ${caseDetails.status === 'active' || caseDetails.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                                        caseDetails.status === 'completed' ? 'bg-emerald-100 text-emerald-700' :
                                            'bg-slate-100 text-slate-700'}`}>
                                    {caseDetails.status?.replace('_', ' ')}
                                </span>
                            </div>
                            <p className="text-slate-500 max-w-2xl">
                                {caseDetails.description || 'No description provided.'}
                            </p>
                        </div>
                        {/* Meta Info */}
                        <div className="flex flex-col gap-2 text-sm text-slate-500 bg-slate-50 p-4 rounded-xl min-w-[200px]">
                            <div className="flex items-center gap-2">
                                <CalendarIcon className="h-4 w-4 text-slate-400" />
                                <span>Created: {new Date(caseDetails.created_at).toLocaleDateString()}</span>
                            </div>
                            {caseDetails.due_date && (
                                <div className="flex items-center gap-2">
                                    <ClockIcon className="h-4 w-4 text-slate-400" />
                                    <span>Due: {new Date(caseDetails.due_date).toLocaleDateString()}</span>
                                </div>
                            )}
                            <div className="flex items-center gap-2">
                                <UserCircleIcon className="h-4 w-4 text-slate-400" />
                                <span>Agent: {caseDetails.assigned_to || 'Unassigned'}</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <BriefcaseIcon className="h-4 w-4 text-slate-400" />
                                <span>Priority: <span className="capitalize">{caseDetails.priority || 'Normal'}</span></span>
                            </div>
                            {caseDetails.updated_at && (
                                <div className="flex items-center gap-2 text-xs text-slate-400 mt-1">
                                    <span>Updated: {new Date(caseDetails.updated_at).toLocaleDateString()}</span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Case Journey Tracker */}
            {caseDetails.stages && caseDetails.stages.length > 0 && (
                <CaseJourneyTracker stages={caseDetails.stages} currentStage={caseDetails.current_stage} />
            )}

            {/* Tabs & Content */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden min-h-[400px]">
                <div className="border-b border-slate-100">
                    <div className="flex gap-1 p-2 overflow-x-auto">
                        {tabs.map(tab => (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`
                                    flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all whitespace-nowrap
                                    ${activeTab === tab.id
                                        ? 'bg-indigo-50 text-indigo-700 shadow-sm'
                                        : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}
                                `}
                            >
                                <tab.icon className={`h-4 w-4 ${activeTab === tab.id ? 'text-indigo-500' : 'text-slate-400'}`} />
                                {tab.label}
                                {tab.count > 0 && (
                                    <span className={`
                                        px-2 py-0.5 rounded-full text-xs
                                        ${activeTab === tab.id ? 'bg-indigo-100 text-indigo-700' : 'bg-slate-100 text-slate-600'}
                                    `}>
                                        {tab.count}
                                    </span>
                                )}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="p-6">
                    {activeTab === 'tasks' && (
                        <div className="space-y-4">
                            {(!caseDetails.tasks || caseDetails.tasks.length === 0) ? (
                                <div className="text-center py-12 text-slate-400">
                                    <CheckCircleIcon className="h-12 w-12 mx-auto mb-3 opacity-20" />
                                    <p>No tasks associated with this case.</p>
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {caseDetails.tasks.map(task => (
                                        <div key={task.id} className="flex items-start gap-4 p-4 rounded-xl border border-slate-100 hover:border-slate-200 transition-colors bg-slate-50/50">
                                            <div className={`mt-1 h-5 w-5 rounded border flex items-center justify-center
                                                ${task.status === 'done' || task.status === 'completed'
                                                    ? 'bg-emerald-500 border-emerald-500 text-white'
                                                    : 'border-slate-300 bg-white'}`}>
                                                {(task.status === 'done' || task.status === 'completed') && <CheckCircleIcon className="h-4 w-4" />}
                                            </div>
                                            <div className="flex-1">
                                                <h4 className={`font-medium ${task.status === 'done' || task.status === 'completed' ? 'text-slate-500 line-through' : 'text-slate-900'}`}>
                                                    {task.title}
                                                </h4>
                                                {task.description && (
                                                    <p className="text-sm text-slate-500 mt-1">{task.description}</p>
                                                )}
                                                {task.due_date && (
                                                    <div className="flex items-center gap-1.5 mt-1 text-xs text-slate-500">
                                                        <ClockIcon className="h-3.5 w-3.5" />
                                                        Due: {new Date(task.due_date).toLocaleDateString()}
                                                    </div>
                                                )}
                                            </div>
                                            <span className={`px-2 py-1 text-xs font-medium rounded-full capitalize
                                                ${task.status === 'done' || task.status === 'completed' ? 'bg-emerald-50 text-emerald-600' :
                                                    task.status === 'in_progress' ? 'bg-blue-50 text-blue-600' :
                                                        'bg-slate-100 text-slate-600'}`}>
                                                {(task.status || 'pending').replace('_', ' ')}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab === 'applications' && (
                        <div className="space-y-8">
                            {/* University Applications */}
                            <div className="space-y-4">
                                <h3 className="text-lg font-medium text-slate-900 flex items-center gap-2">
                                    <BuildingLibraryIcon className="h-5 w-5 text-indigo-500" />
                                    University Applications
                                </h3>
                                {(!caseDetails.uni_applications || caseDetails.uni_applications.length === 0) ? (
                                    <p className="text-slate-500 text-sm italic">No university applications linked to this case.</p>
                                ) : (
                                    <div className="grid gap-3">
                                        {caseDetails.uni_applications.map(app => (
                                            <button
                                                key={app.id}
                                                onClick={() => navigate(`/portal/applications/${app.id}`)}
                                                className="flex items-center justify-between p-4 rounded-xl border border-slate-100 hover:border-indigo-100 hover:bg-slate-50 transition-all group text-left w-full"
                                            >
                                                <div className="flex items-center gap-4 flex-1">
                                                    <div className="h-10 w-10 rounded-lg bg-indigo-50 flex items-center justify-center text-indigo-600 group-hover:bg-indigo-100 transition-colors">
                                                        <BuildingLibraryIcon className="h-6 w-6" />
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <h4 className="font-medium text-slate-900 group-hover:text-indigo-700 transition-colors truncate">
                                                            {app.university_name || 'Unknown University'}
                                                        </h4>
                                                        <div className="flex items-center gap-2 text-sm text-slate-500">
                                                            <span className="truncate">{app.program_name || 'Unknown Program'}</span>
                                                            {app.intake_term && (
                                                                <span className="text-xs text-slate-400">• {app.intake_term}</span>
                                                            )}
                                                        </div>
                                                        {app.doc_progress?.total > 0 && (
                                                            <div className="mt-2 max-w-[200px]">
                                                                <DocumentProgress done={app.doc_progress.done} total={app.doc_progress.total} />
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-3">
                                                    <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium capitalize
                                                        ${app.status === 'offer_accepted' ? 'bg-emerald-100 text-emerald-700' :
                                                            app.status === 'submitted' ? 'bg-blue-100 text-blue-700' :
                                                                'bg-slate-100 text-slate-600'}`}>
                                                        {app.status?.replace('_', ' ')}
                                                    </span>
                                                    <ChevronRightIcon className="h-4 w-4 text-slate-400 group-hover:text-indigo-400" />
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <div className="h-px bg-slate-100" />

                            {/* Visa Applications */}
                            <div className="space-y-4">
                                <h3 className="text-lg font-medium text-slate-900 flex items-center gap-2">
                                    <GlobeEuropeAfricaIcon className="h-5 w-5 text-emerald-500" />
                                    Visa Applications
                                </h3>
                                {(!caseDetails.visa_applications || caseDetails.visa_applications.length === 0) ? (
                                    <p className="text-slate-500 text-sm italic">No visa applications linked to this case.</p>
                                ) : (
                                    <div className="grid gap-3">
                                        {caseDetails.visa_applications.map(app => (
                                            <div
                                                key={app.id}
                                                className="flex items-center justify-between p-4 rounded-xl border border-slate-100 bg-white"
                                            >
                                                <div className="flex items-center gap-4 flex-1">
                                                    <div className="h-10 w-10 rounded-lg bg-emerald-50 flex items-center justify-center text-emerald-600">
                                                        <GlobeEuropeAfricaIcon className="h-6 w-6" />
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <h4 className="font-medium text-slate-900">
                                                            {app.country || 'Unknown Country'}
                                                        </h4>
                                                        <div className="flex items-center gap-2 text-sm text-slate-500">
                                                            <span>{app.visa_type || 'Unknown Visa Type'}</span>
                                                        </div>
                                                        {app.doc_progress?.total > 0 && (
                                                            <div className="mt-2 max-w-[200px]">
                                                                <DocumentProgress done={app.doc_progress.done} total={app.doc_progress.total} />
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                                <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium capitalize
                                                    ${app.status === 'approved' ? 'bg-emerald-100 text-emerald-700' :
                                                        app.status === 'submitted' ? 'bg-blue-100 text-blue-700' :
                                                            'bg-slate-100 text-slate-600'}`}>
                                                    {app.status?.replace('_', ' ')}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {activeTab === 'offers' && (
                        <div className="space-y-4">
                            {(!caseDetails.offers || caseDetails.offers.length === 0) ? (
                                <div className="text-center py-12 text-slate-400">
                                    <GiftIcon className="h-12 w-12 mx-auto mb-3 opacity-20" />
                                    <p>No offers received yet.</p>
                                    <p className="text-sm mt-1">Offers will appear here once universities respond to your applications.</p>
                                </div>
                            ) : (
                                <div className="grid gap-4 md:grid-cols-2">
                                    {caseDetails.offers.map(offer => (
                                        <OfferCard key={offer.id} offer={offer} />
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab === 'documents' && (
                        <div className="space-y-6">
                            {totalDocs.total === 0 ? (
                                <div className="text-center py-12 text-slate-400">
                                    <DocumentTextIcon className="h-12 w-12 mx-auto mb-3 opacity-20" />
                                    <p>No documents found for this case.</p>
                                </div>
                            ) : (
                                <>
                                    {/* Overall Progress */}
                                    <div className="bg-slate-50 rounded-xl p-4">
                                        <div className="flex items-center justify-between mb-2">
                                            <span className="text-sm font-medium text-slate-700">Overall Document Progress</span>
                                            <span className="text-sm text-slate-500">{totalDocs.done} of {totalDocs.total} complete</span>
                                        </div>
                                        <div className="h-3 bg-slate-200 rounded-full overflow-hidden">
                                            <div
                                                className={`h-full transition-all ${totalDocs.done === totalDocs.total ? 'bg-emerald-500' : 'bg-indigo-500'}`}
                                                style={{ width: `${totalDocs.total > 0 ? (totalDocs.done / totalDocs.total) * 100 : 0}%` }}
                                            />
                                        </div>
                                    </div>

                                    {/* University App Documents */}
                                    {caseDetails.uni_applications?.filter(a => a.doc_progress?.total > 0).map(app => (
                                        <div key={app.id} className="border border-slate-100 rounded-xl p-4">
                                            <div className="flex items-center gap-3 mb-3">
                                                <BuildingLibraryIcon className="h-5 w-5 text-indigo-500" />
                                                <div>
                                                    <h4 className="font-medium text-slate-900">{app.university_name}</h4>
                                                    <p className="text-sm text-slate-500">{app.program_name}</p>
                                                </div>
                                            </div>
                                            <DocumentProgress done={app.doc_progress.done} total={app.doc_progress.total} label="documents" />
                                        </div>
                                    ))}

                                    {/* Visa App Documents */}
                                    {caseDetails.visa_applications?.filter(a => a.doc_progress?.total > 0).map(app => (
                                        <div key={app.id} className="border border-slate-100 rounded-xl p-4">
                                            <div className="flex items-center gap-3 mb-3">
                                                <GlobeEuropeAfricaIcon className="h-5 w-5 text-emerald-500" />
                                                <div>
                                                    <h4 className="font-medium text-slate-900">{app.country}</h4>
                                                    <p className="text-sm text-slate-500">{app.visa_type}</p>
                                                </div>
                                            </div>
                                            <DocumentProgress done={app.doc_progress.done} total={app.doc_progress.total} label="documents" />
                                        </div>
                                    ))}
                                </>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ClientCaseDetailPage;
